/**
 * Poseidon dashboard — the persistent top status ribbon of §3.2 A.
 *
 * Renders exactly the seven fields the spec lists, in spec order:
 *   MT5 · Account (DEMO, masked) · Execution · Session · Risk ·
 *   Data freshness · DeepSeek
 *
 * A non-normal halt is additionally promoted to a full-width strip, because a
 * halted system must not look like a normal one at a glance.
 */
import { Link } from 'react-router-dom';
import type { RibbonStatus } from '../api/types';
import {
  DEEPSEEK_LABELS,
  EXECUTION_MODE_LABELS,
  HALT_LABELS,
  MT5_LABELS,
  RISK_STATE_LABELS,
  SESSION_PHASE_LABELS,
  toneForDeepSeek,
  toneForExecutionMode,
  toneForMt5,
  toneForRiskState,
  toneForSessionPhase,
} from '../lib/labels';
import { formatAge, formatDateTimeWithZone, formatInteger } from '../lib/format';
import { cx } from '../lib/cx';
import type { Tone } from '../lib/staleness';
import { Badge } from './primitives';
import { StalenessBadge } from './Staleness';

function RibbonField({
  label,
  value,
  detail,
  tone,
  title,
}: {
  label: string;
  value: string;
  detail?: string;
  tone: Tone;
  title?: string;
}): JSX.Element {
  return (
    <div className="ribbon__field" title={title}>
      <span className="ribbon__label">{label}</span>
      <span className="ribbon__value">
        <Badge tone={tone}>{value}</Badge>
      </span>
      {detail !== undefined && <span className="ribbon__detail">{detail}</span>}
    </div>
  );
}

export function StatusRibbon({ ribbon }: { ribbon: RibbonStatus }): JSX.Element {
  const freshness = ribbon.data_freshness;
  const account = ribbon.account;

  return (
    <div className="grid" style={{ gap: 10 }}>
      <div className="ribbon">
        <RibbonField
          label="MT5"
          value={MT5_LABELS[ribbon.mt5]}
          tone={toneForMt5(ribbon.mt5)}
          detail={
            ribbon.mt5_terminal.last_heartbeat_at === null
              ? 'no authenticated heartbeat yet'
              : `heartbeat ${formatAge(Date.now() - new Date(ribbon.mt5_terminal.last_heartbeat_at).getTime())} · build ${ribbon.mt5_terminal.terminal_build ?? '—'}`
          }
          title="MT5 terminal and EA bridge connection state."
        />

        <RibbonField
          label="Account"
          value={`${account.mode.toUpperCase()} — ${account.account_number_masked}`}
          tone={account.mode === 'demo' && account.demo_verified ? 'ok' : 'danger'}
          detail={`${account.broker_server} · ${account.margin_mode} · 1:${formatInteger(account.leverage)} · ${account.account_currency}`}
          title={
            account.demo_verified
              ? 'Account mode positively verified as demo. A non-demo account fails closed (§7.1).'
              : 'DANGER: the daemon has not verified that this is a demo account. Execution must stay disabled.'
          }
        />

        <RibbonField
          label="Execution"
          value={EXECUTION_MODE_LABELS[ribbon.execution_mode]}
          tone={toneForExecutionMode(ribbon.execution_mode)}
          detail={
            ribbon.execution_mode === 'approval_required'
              ? 'every order waits for an operator click'
              : ribbon.execution_mode === 'demo_enabled'
                ? 'constrained automated execution'
                : 'no order submission possible'
          }
          title="§19.4 execution_mode. approval_required is the default until the Phase 3 gate is cleared."
        />

        <RibbonField
          label="Session"
          value={SESSION_PHASE_LABELS[ribbon.session.phase]}
          tone={toneForSessionPhase(ribbon.session.phase)}
          detail={`${ribbon.session.label} · broker ${formatDateTimeWithZone(ribbon.session.broker_time, ribbon.session.broker_timezone)}`}
          title="§7.3 session lifecycle."
        />

        <RibbonField
          label="Risk"
          value={RISK_STATE_LABELS[ribbon.risk_state]}
          tone={toneForRiskState(ribbon.risk_state)}
          detail={
            ribbon.halt.level === 'none'
              ? `no halt · flatten ${ribbon.session.flatten_deadline_at === null ? '—' : formatDateTimeWithZone(ribbon.session.flatten_deadline_at, ribbon.session.broker_timezone)}`
              : `${HALT_LABELS[ribbon.halt.level]} since ${formatDateTimeWithZone(ribbon.halt.activated_at)}`
          }
          title="§7.3 soft halt blocks new entries; hard halt blocks everything except the emergency-close workflow."
        />

        <RibbonField
          label="Data freshness"
          value={`tick ${formatAge(freshness.tick_age_ms)}`}
          tone="info"
          detail={`bar ${formatAge(freshness.bar_age_ms)} · drift ${freshness.clock_drift_ms === null ? '—' : `${freshness.clock_drift_ms.toFixed(0)} ms`}`}
          title="§7.1 quote staleness gate. The indicator beside the timestamp turns alarming when any feed is stale."
        />

        <RibbonField
          label="DeepSeek"
          value={DEEPSEEK_LABELS[ribbon.deepseek.health]}
          tone={toneForDeepSeek(ribbon.deepseek.health)}
          detail={
            ribbon.deepseek.last_latency_ms === null
              ? 'no calls this session'
              : `last ok ${ribbon.deepseek.last_ok_at === null ? '—' : formatDateTimeWithZone(ribbon.deepseek.last_ok_at)} · ${ribbon.deepseek.last_latency_ms} ms · model ${ribbon.deepseek.model_id ?? '—'}`
          }
          title="Research layer health. Execution never depends on it: unavailable, stale or invalid research becomes a neutral adjustment (§1.4)."
        />
      </div>

      <div className="ribbon" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        <div className="ribbon__field">
          <span className="ribbon__label">Pre-flight readiness</span>
          <span className="ribbon__value">
            <Badge tone={ribbon.readiness === 'ready' ? 'ok' : ribbon.readiness === 'degraded' ? 'warn' : 'danger'}>
              {ribbon.readiness}
            </Badge>
            <Link to="/operations" className="text-faint" style={{ fontSize: 11 }}>
              details
            </Link>
          </span>
        </div>
        <div className="ribbon__field">
          <span className="ribbon__label">Snapshot age</span>
          <span className="ribbon__value">
            <StalenessBadge lastAt={ribbon.as_of} thresholds={{ aging: freshness.thresholds.snapshot_aging_ms, stale: freshness.thresholds.snapshot_stale_ms }} />
          </span>
        </div>
        <div className="ribbon__field">
          <span className="ribbon__label">Active alerts</span>
          <span className="ribbon__value">
            <Badge tone={ribbon.alerts.some((alert) => alert.severity === 'critical') ? 'danger' : ribbon.alerts.length > 0 ? 'warn' : 'ok'}>
              {ribbon.alerts.length}
            </Badge>
          </span>
          <span className="ribbon__detail">
            {ribbon.alerts[0]?.message ?? 'no operator alerts'}
          </span>
        </div>
      </div>

      {ribbon.halt.level !== 'none' && (
        <div className={cx('halt-strip', ribbon.halt.level === 'hard' ? 'halt-strip--hard' : 'halt-strip--soft')} role="alert">
          <strong>{HALT_LABELS[ribbon.halt.level]}</strong>
          <span>
            {ribbon.halt.reason ?? 'no reason recorded'}
            {ribbon.halt.activated_by !== null && <> · set by {ribbon.halt.activated_by}</>}
            {ribbon.halt.activated_at !== null && <> at {formatDateTimeWithZone(ribbon.halt.activated_at)}</>}
          </span>
          <span className="text-muted" style={{ marginLeft: 'auto' }}>
            {ribbon.halt.level === 'hard'
              ? 'All submissions blocked except the explicit emergency-close workflow.'
              : 'New entries blocked; protective changes, exits and cancels still allowed.'}
          </span>
        </div>
      )}
    </div>
  );
}
